import React, { useEffect, useState } from "react";
import { Navigate } from "react-router-dom";
import { api, formatINR, formatApiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";
import { Trash2, Plus, Save, X } from "lucide-react";

const TABS = ["overview", "programs", "sessions", "bookings", "leads"];

export default function Admin() {
  const { user, ready } = useAuth();
  const [tab, setTab] = useState("overview");

  if (!ready) return <div className="p-20 font-mono text-xs tracking-[0.3em] uppercase text-white/40">Loading…</div>;
  if (!user || user.role !== "admin") return <Navigate to="/login?next=/admin" replace />;

  return (
    <div data-testid="admin-page" className="min-h-[calc(100vh-4rem)] bg-novya-dark text-white">
      <div className="max-w-[1400px] mx-auto px-6 md:px-10 py-16">
        <div className="font-mono text-[10px] tracking-[0.3em] uppercase text-white/40">Admin console</div>
        <h1 className="mt-4 font-display font-black text-5xl md:text-6xl tracking-tighter leading-[0.95]">
          Command<span className="text-novya-accent">.</span>
        </h1>

        <div className="mt-12 border-b border-white/10 flex gap-1">
          {TABS.map((t) => (
            <button
              key={t}
              data-testid={`admin-tab-${t}`}
              onClick={() => setTab(t)}
              className={`px-5 py-3 font-mono text-xs tracking-[0.2em] uppercase transition-colors border-b-2 ${
                tab === t ? "border-novya-accent text-white" : "border-transparent text-white/40 hover:text-white/70"
              }`}
            >
              {t}
            </button>
          ))}
        </div>

        <div className="mt-10">
          {tab === "overview" && <AdminOverview />}
          {tab === "programs" && <AdminPrograms />}
          {tab === "sessions" && <AdminSessions />}
          {tab === "bookings" && <AdminBookings />}
          {tab === "leads" && <AdminLeads />}
        </div>
      </div>
    </div>
  );
}

function AdminOverview() {
  const [stats, setStats] = useState(null);
  useEffect(() => { api.get("/admin/stats").then((r) => setStats(r.data)).catch(() => {}); }, []);
  if (!stats) return <div className="font-mono text-xs tracking-[0.3em] uppercase text-white/40">Loading stats…</div>;
  const items = [
    { k: "Users", v: stats.users },
    { k: "Enrollments", v: stats.enrollments },
    { k: "Paid transactions", v: stats.paid_transactions },
    { k: "Sessions", v: stats.sessions },
    { k: "Leads", v: stats.leads },
  ];
  return (
    <div data-testid="admin-overview" className="grid grid-cols-2 md:grid-cols-5 gap-px bg-white/10 border border-white/10">
      {items.map((i) => (
        <div key={i.k} className="bg-novya-dark p-6">
          <div className="font-mono text-[10px] tracking-[0.3em] uppercase text-white/40">{i.k}</div>
          <div className="mt-3 font-display font-black text-3xl md:text-4xl">{i.v}</div>
        </div>
      ))}
    </div>
  );
}

function AdminPrograms() {
  const [programs, setPrograms] = useState([]);
  const [editing, setEditing] = useState(null); // program slug
  const [draft, setDraft] = useState({});
  const [saving, setSaving] = useState(false);

  const load = () => api.get("/admin/programs").then((r) => setPrograms(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const openEdit = (p) => {
    setEditing(p.slug);
    setDraft({
      tagline: p.tagline || "",
      duration: p.duration || "",
      format: p.format || "",
      price_inr: p.price_inr || 0,
      hero_image: p.hero_image || "",
    });
  };

  const save = async () => {
    setSaving(true);
    try {
      await api.patch(`/admin/programs/${editing}`, {
        tagline: draft.tagline,
        duration: draft.duration,
        format: draft.format,
        price_inr: Number(draft.price_inr),
        hero_image: draft.hero_image,
      });
      toast.success("Program updated");
      setEditing(null);
      await load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Update failed");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div data-testid="admin-programs">
      <div className="grid grid-cols-1 gap-px bg-white/10 border border-white/10">
        {programs.map((p) => (
          <div key={p.slug} className="bg-novya-dark p-6" data-testid={`admin-program-${p.slug}`}>
            {editing === p.slug ? (
              <div>
                <div className="flex justify-between items-baseline mb-4">
                  <h3 className="font-display font-black text-2xl">{p.title}</h3>
                  <button onClick={() => setEditing(null)} className="text-white/60 hover:text-white"><X size={18} /></button>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                  <Field label="Tagline" value={draft.tagline} onChange={(v) => setDraft({ ...draft, tagline: v })} testid={`edit-tagline-${p.slug}`} />
                  <Field label="Duration" value={draft.duration} onChange={(v) => setDraft({ ...draft, duration: v })} testid={`edit-duration-${p.slug}`} />
                  <Field label="Format" value={draft.format} onChange={(v) => setDraft({ ...draft, format: v })} />
                  <Field label="Price (INR)" type="number" value={draft.price_inr} onChange={(v) => setDraft({ ...draft, price_inr: v })} testid={`edit-price-${p.slug}`} />
                  <Field label="Hero image URL" value={draft.hero_image} onChange={(v) => setDraft({ ...draft, hero_image: v })} full />
                </div>
                <button
                  onClick={save}
                  disabled={saving}
                  data-testid={`save-program-${p.slug}`}
                  className="mt-6 inline-flex items-center gap-2 px-5 py-2.5 bg-novya-accent text-white font-medium text-sm hover:opacity-90 disabled:opacity-50"
                >
                  <Save size={14} /> {saving ? "Saving…" : "Save"}
                </button>
              </div>
            ) : (
              <div className="flex justify-between items-start gap-6">
                <div className="flex-1">
                  <h3 className="font-display font-black text-2xl tracking-tight">{p.title}</h3>
                  <p className="mt-2 text-sm text-white/60 leading-relaxed">{p.tagline}</p>
                  <div className="mt-3 flex gap-6 font-mono text-xs text-white/50">
                    <span>{p.duration}</span>
                    <span>{p.format}</span>
                    <span className="text-novya-accent">{formatINR(p.price_inr)}</span>
                  </div>
                </div>
                <button
                  onClick={() => openEdit(p)}
                  data-testid={`edit-program-${p.slug}`}
                  className="px-4 py-2 border border-white/20 text-white text-xs font-medium hover:bg-white hover:text-novya-dark transition-colors"
                >
                  Edit
                </button>
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

function AdminSessions() {
  const [sessions, setSessions] = useState([]);
  const [programs, setPrograms] = useState([]);
  const [creating, setCreating] = useState(false);
  const [draft, setDraft] = useState({ program_slug: "", title: "", description: "", starts_at: "", duration_minutes: 60, meeting_url: "" });

  const load = () => api.get("/admin/sessions").then((r) => setSessions(r.data)).catch(() => {});
  useEffect(() => {
    api.get("/admin/programs").then((r) => {
      setPrograms(r.data);
      setDraft((d) => ({ ...d, program_slug: r.data[0]?.slug || "" }));
    }).catch(() => {});
    load();
  }, []);

  const create = async () => {
    if (!draft.program_slug || !draft.title || !draft.starts_at) {
      toast.error("Fill program, title, and start time");
      return;
    }
    try {
      // Convert local datetime-local value → ISO with timezone
      const iso = new Date(draft.starts_at).toISOString();
      await api.post("/admin/sessions", { ...draft, starts_at: iso, duration_minutes: Number(draft.duration_minutes) });
      toast.success("Session created");
      setCreating(false);
      setDraft({ program_slug: programs[0]?.slug || "", title: "", description: "", starts_at: "", duration_minutes: 60, meeting_url: "" });
      await load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Failed to create");
    }
  };

  const del = async (id) => {
    if (!window.confirm("Delete this session?")) return;
    try {
      await api.delete(`/admin/sessions/${id}`);
      toast.success("Session deleted");
      await load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Delete failed");
    }
  };

  return (
    <div data-testid="admin-sessions">
      <div className="flex justify-between items-center mb-6">
        <h2 className="font-display font-bold text-2xl tracking-tight">Cohort sessions</h2>
        <button
          onClick={() => setCreating(!creating)}
          data-testid="new-session-btn"
          className="inline-flex items-center gap-2 px-5 py-2.5 bg-novya-accent text-white font-medium text-sm hover:opacity-90"
        >
          <Plus size={14} /> New session
        </button>
      </div>

      {creating && (
        <div className="border border-white/10 p-6 mb-6" data-testid="new-session-form">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
            <div>
              <label className="font-mono text-[10px] tracking-[0.3em] uppercase text-white/40">Program</label>
              <select
                value={draft.program_slug}
                onChange={(e) => setDraft({ ...draft, program_slug: e.target.value })}
                data-testid="session-program-select"
                className="mt-2 w-full bg-transparent border-b border-white/30 py-2 outline-none focus:border-novya-accent text-white"
              >
                {programs.map((p) => <option key={p.slug} value={p.slug} className="text-novya-dark">{p.title}</option>)}
              </select>
            </div>
            <Field dark label="Title" value={draft.title} onChange={(v) => setDraft({ ...draft, title: v })} testid="session-title-input" />
            <Field dark label="Description" value={draft.description} onChange={(v) => setDraft({ ...draft, description: v })} />
            <div>
              <label className="font-mono text-[10px] tracking-[0.3em] uppercase text-white/40">Starts at</label>
              <input
                type="datetime-local"
                value={draft.starts_at}
                onChange={(e) => setDraft({ ...draft, starts_at: e.target.value })}
                data-testid="session-starts-input"
                className="mt-2 w-full bg-transparent border-b border-white/30 py-2 outline-none focus:border-novya-accent text-white"
              />
            </div>
            <Field dark label="Duration (min)" type="number" value={draft.duration_minutes} onChange={(v) => setDraft({ ...draft, duration_minutes: v })} />
            <Field dark label="Meeting URL" value={draft.meeting_url} onChange={(v) => setDraft({ ...draft, meeting_url: v })} full />
          </div>
          <div className="mt-4 flex gap-3">
            <button onClick={create} data-testid="save-session-btn" className="px-5 py-2 bg-novya-accent text-white text-sm font-medium">Create</button>
            <button onClick={() => setCreating(false)} className="px-5 py-2 border border-white/20 text-sm">Cancel</button>
          </div>
        </div>
      )}

      {sessions.length === 0 ? (
        <div className="border border-white/10 p-10 text-white/50 font-mono text-xs tracking-[0.2em] uppercase" data-testid="admin-sessions-empty">
          No sessions yet
        </div>
      ) : (
        <div className="border border-white/10 divide-y divide-white/10">
          {sessions.map((s) => (
            <div key={s._id} className="p-5 grid grid-cols-12 gap-4 items-center" data-testid={`admin-session-row-${s._id}`}>
              <div className="col-span-3 font-mono text-xs text-white/60">{new Date(s.starts_at).toLocaleString("en-IN")}</div>
              <div className="col-span-3 text-sm">{s.title}</div>
              <div className="col-span-3 font-mono text-xs text-white/50">{s.program_slug}</div>
              <div className="col-span-2 font-mono text-xs text-white/50">{s.duration_minutes}min</div>
              <div className="col-span-1 flex justify-end">
                <button onClick={() => del(s._id)} data-testid={`delete-session-${s._id}`} className="text-white/40 hover:text-red-400"><Trash2 size={16} /></button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function AdminLeads() {
  const [leads, setLeads] = useState([]);
  useEffect(() => { api.get("/admin/leads").then((r) => setLeads(r.data)).catch(() => {}); }, []);

  return (
    <div data-testid="admin-leads">
      <h2 className="font-display font-bold text-2xl tracking-tight mb-6">Leads · {leads.length}</h2>
      {leads.length === 0 ? (
        <div className="border border-white/10 p-10 text-white/50 font-mono text-xs tracking-[0.2em] uppercase">No leads yet</div>
      ) : (
        <div className="border border-white/10 divide-y divide-white/10">
          {leads.map((l) => (
            <div key={l._id} className="p-5 grid grid-cols-12 gap-4" data-testid={`admin-lead-${l._id}`}>
              <div className="col-span-2 font-mono text-xs text-white/50">{new Date(l.created_at).toLocaleDateString("en-IN")}</div>
              <div className="col-span-3">
                <div className="text-sm">{l.name}</div>
                <div className="font-mono text-xs text-white/50">{l.email}</div>
              </div>
              <div className="col-span-3 font-mono text-xs text-white/60">{l.program_interest || "-"}</div>
              <div className="col-span-2 font-mono text-[10px] tracking-[0.2em] uppercase text-novya-accent">{l.source || "contact"}</div>
              <div className="col-span-2 text-xs text-white/60">{l.message || ""}</div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function AdminBookings() {
  const [bookings, setBookings] = useState([]);
  useEffect(() => { api.get("/admin/bookings").then((r) => setBookings(r.data)).catch(() => {}); }, []);

  const fmt = (iso) => new Date(iso).toLocaleString("en-IN", {
    weekday: "short", day: "numeric", month: "short",
    hour: "numeric", minute: "2-digit", hour12: true, timeZone: "Asia/Kolkata",
  });

  return (
    <div data-testid="admin-bookings">
      <h2 className="font-display font-bold text-2xl tracking-tight mb-6">Advisor bookings · {bookings.length}</h2>
      {bookings.length === 0 ? (
        <div className="border border-white/10 p-10 text-white/50 font-mono text-xs tracking-[0.2em] uppercase" data-testid="admin-bookings-empty">
          No bookings yet
        </div>
      ) : (
        <div className="border border-white/10 divide-y divide-white/10">
          {bookings.map((b) => (
            <div key={b._id} className="p-5 grid grid-cols-12 gap-4" data-testid={`admin-booking-${b._id}`}>
              <div className="col-span-3 font-mono text-xs text-white/60">{fmt(b.slot_start)} IST</div>
              <div className="col-span-3">
                <div className="text-sm">{b.name}</div>
                <div className="font-mono text-xs text-white/50">{b.email}</div>
              </div>
              <div className="col-span-2 font-mono text-xs text-white/60">{b.phone}</div>
              <div className="col-span-2 font-mono text-xs text-white/60">{b.program_slug || "-"}</div>
              <div className="col-span-2 font-mono text-[10px] tracking-[0.2em] uppercase text-novya-accent">{b.status}</div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function Field({ label, value, onChange, type = "text", full = false, dark = false, testid }) {
  return (
    <div className={full ? "md:col-span-2" : ""}>
      <label className={`font-mono text-[10px] tracking-[0.3em] uppercase ${dark ? "text-white/40" : "text-white/40"}`}>{label}</label>
      <input
        type={type}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        data-testid={testid}
        className={`mt-2 w-full bg-transparent border-b border-white/30 py-2 outline-none focus:border-novya-accent text-white`}
      />
    </div>
  );
}
